import { isSameOriginUrl } from '@Core/lib/tasks/utils/navigate';
import { createContext, useContext, useSyncExternalStore } from 'react';

export type BreadcrumbLocation = {
  /** Path without the query string or hash. One location exists for each path. */
  path: string;
  /** Path, query string, and hash that the visitor last viewed at this path. */
  url: string;
};

const MAX_LOCATIONS = 50;
const STORAGE_KEY_PREFIX = 'diagonal:breadcrumb-locations:';

const listeners = new Set<() => void>();

/**
 * The workspace whose stored locations `Sdk.Components.Breadcrumb` reads. It is null outside a
 * page document, such as in the page playground or the editor preview.
 */
export const BreadcrumbLocationContext = createContext<string | null>(null);

function storageKey(workspaceId: string): string {
  return `${STORAGE_KEY_PREFIX}${workspaceId}`;
}

function isLocation(value: unknown): value is BreadcrumbLocation {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const location = value as Record<string, unknown>;

  return typeof location.path === 'string' && typeof location.url === 'string';
}

function parseLocations(raw: string | null): BreadcrumbLocation[] {
  if (raw === null) {
    return [];
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    if (error instanceof SyntaxError) {
      return [];
    }

    throw error;
  }

  return Array.isArray(parsed) && parsed.every(isLocation) ? parsed : [];
}

function readRaw(workspaceId: string): string | null {
  return window.sessionStorage.getItem(storageKey(workspaceId));
}

function writeLocations(workspaceId: string, locations: BreadcrumbLocation[]): void {
  const raw = JSON.stringify(locations);

  if (raw === readRaw(workspaceId)) {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey(workspaceId), raw);
  } catch (error) {
    // A full store keeps the previous locations. Breadcrumbs are a convenience and must not
    // break navigation.
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      return;
    }

    throw error;
  }

  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Stores the browser's current location as the URL last viewed at its path. Only the most
 * recently viewed paths are kept.
 */
export function rememberBreadcrumbLocation(workspaceId: string): void {
  const path = window.location.pathname;
  const url = `${path}${window.location.search}${window.location.hash}`;
  const otherLocations = parseLocations(readRaw(workspaceId)).filter(
    (location) => location.path !== path
  );

  writeLocations(workspaceId, [...otherLocations, { path, url }].slice(-MAX_LOCATIONS));
}

/**
 * Returns the URL that the visitor last viewed at the path of `href`. A link with its own query
 * string or hash, a link to another origin, and a path without a stored location are returned
 * unchanged.
 */
export function resolveBreadcrumbHref(locations: BreadcrumbLocation[], href: string): string {
  if (!isSameOriginUrl(href)) {
    return href;
  }

  const url = new URL(href, window.location.href);

  if (url.search !== '' || url.hash !== '') {
    return href;
  }

  return locations.find((location) => location.path === url.pathname)?.url ?? href;
}

function getServerSnapshot(): string | null {
  return null;
}

/**
 * Returns a function that resolves a crumb's link with `resolveBreadcrumbHref`. Outside a page
 * document, every link resolves to itself.
 */
export function useBreadcrumbHrefResolver(): (href: string) => string {
  const workspaceId = useContext(BreadcrumbLocationContext);
  const raw = useSyncExternalStore(
    subscribe,
    () => (workspaceId === null ? null : readRaw(workspaceId)),
    getServerSnapshot
  );
  const locations = parseLocations(raw);

  return (href) => resolveBreadcrumbHref(locations, href);
}
